/**
 * Piece Rotation Utility Module
 *
 * Provides functions to rotate tetromino pieces in 90-degree increments.
 * Uses matrix rotation formulas:
 * - Clockwise 90°: (x, y) -> (y, -x)
 * - Counter-clockwise 90°: (x, y) -> (-y, x)
 *
 * After rotation, shapes are normalized to start at origin (0,0)
 */

/**
 * Rotate a coordinate 90 degrees clockwise or counter-clockwise
 * @param {Object} coord - Coordinate with x and y properties
 * @param {string} direction - 'cw' for clockwise, 'ccw' for counter-clockwise
 * @returns {Object} Rotated coordinate
 */
export function getRotatedCoordinates(coord, direction = 'cw') {
  const { x, y } = coord;

  if (direction === 'ccw') {
    // Counter-clockwise: (x, y) -> (-y, x)
    return { x: -y, y: x };
  }

  // Clockwise (default): (x, y) -> (y, -x)
  return { x: y, y: -x };
}

/**
 * Normalize a shape to start at origin (0,0)
 * Translates all coordinates so the minimum x and y are 0
 * @param {Array<{x: number, y: number}>} shape - Array of coordinates
 * @returns {Array<{x: number, y: number}>} Normalized shape
 */
export function normalizeShape(shape) {
  if (!shape || shape.length === 0) {
    return [];
  }

  // Create a copy to avoid mutation
  const shapeCopy = shape.map((coord) => ({ ...coord }));

  // Find minimum x and y
  const minX = Math.min(...shapeCopy.map((c) => c.x));
  const minY = Math.min(...shapeCopy.map((c) => c.y));

  // Translate all coordinates
  return shapeCopy.map((coord) => ({
    x: coord.x - minX,
    y: coord.y - minY,
  }));
}

/**
 * Rotate a piece shape 90 degrees clockwise
 * @param {Array<{x: number, y: number}>} shape - Array of coordinates defining the shape
 * @returns {Array<{x: number, y: number}>} Rotated and normalized shape
 */
export function rotatePieceClockwise(shape) {
  if (!shape || shape.length === 0) {
    return [];
  }

  // Rotate each coordinate
  const rotated = shape.map((coord) => getRotatedCoordinates(coord, 'cw'));

  // Normalize to start at origin
  return normalizeShape(rotated);
}

/**
 * Rotate a piece shape 90 degrees counter-clockwise
 * @param {Array<{x: number, y: number}>} shape - Array of coordinates defining the shape
 * @returns {Array<{x: number, y: number}>} Rotated and normalized shape
 */
export function rotatePieceCounterClockwise(shape) {
  if (!shape || shape.length === 0) {
    return [];
  }

  // Rotate each coordinate
  const rotated = shape.map((coord) => getRotatedCoordinates(coord, 'ccw'));

  // Normalize to start at origin
  return normalizeShape(rotated);
}

/**
 * Rotate a piece shape in the specified direction
 * @param {Array<{x: number, y: number}>} shape - Array of coordinates defining the shape
 * @param {string} direction - 'cw' for clockwise, 'ccw' for counter-clockwise (default: 'cw')
 * @returns {Array<{x: number, y: number}>} Rotated and normalized shape
 */
export function rotatePiece(shape, direction = 'cw') {
  if (direction === 'ccw') {
    return rotatePieceCounterClockwise(shape);
  }

  return rotatePieceClockwise(shape);
}
